import { Component, computed, inject, OnInit } from '@angular/core';
import { Card } from 'primeng/card';
import { DatePipe, DecimalPipe } from '@angular/common';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ProgressBar } from 'primeng/progressbar';
import { BillingService } from '../services/billing.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { UserService } from '../services/user.service';
import { AddTokensDialogComponent } from './add-tokens-dialog/add-tokens-dialog.component';
import { OnDemandSettingsDialogComponent } from './on-demand-settings-dialog/on-demand-settings-dialog.component';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { TagModule } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import {
  allocationTypeLabel,
  relativeTimestamp,
  tokenTypeLabel,
  useTypeLabel,
  useTypeSeverity,
} from './billing-labels';
import { TokenAllocation } from '../../models/token-allocation.model';
import {
  TokenUseEvent,
  TokenUseTypeKey,
} from '../../models/token-use-event.model';

@Component({
  selector: 'app-billing-page',
  imports: [
    Card,
    DatePipe,
    DecimalPipe,
    ProgressBar,
    TableModule,
    ButtonModule,
    AddTokensDialogComponent,
    OnDemandSettingsDialogComponent,
    Paginator,
    TagModule,
    Tooltip,
    RouterLink,
    SharedConfirmDialogComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './billing-page.component.html',
  styleUrl: './billing-page.component.scss',
})
export class BillingPageComponent implements OnInit {
  settingsService = inject(SettingsService);
  billingService = inject(BillingService);
  userService = inject(UserService);
  private confirmationService = inject(ConfirmationService);

  tokenTypeLabel = tokenTypeLabel;
  allocationTypeLabel = allocationTypeLabel;
  useTypeLabel = useTypeLabel;
  useTypeSeverity = useTypeSeverity;
  relativeTimestamp = relativeTimestamp;

  ngOnInit(): void {
    void this.settingsService.getSettings();
    this.billingService.tokenSettings.reload();
    this.billingService.tokenAllocations.reload();
    this.billingService.tokenUseEvents.reload();
  }

  settings = toSignal(
    this.settingsService.settings$.pipe(
      filter((settings) => settings !== null),
    ),
  );

  nextResetDate = computed(
    () => this.settings()?.next_billing_reset_date ?? null,
  );

  periodStartDate = computed(
    () => this.settings()?.current_billing_period_start ?? null,
  );

  daysToReset = computed(() => {
    const nextResetDate = this.nextResetDate();
    if (!nextResetDate) {
      return null;
    }
    const now = new Date();
    const resetDate = new Date(nextResetDate);
    const diffTime = resetDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  billingCycleProgress = computed(() => {
    const periodStart = this.periodStartDate();
    const nextReset = this.nextResetDate();
    if (!periodStart || !nextReset) {
      return null;
    }
    const start = new Date(periodStart).getTime();
    const end = new Date(nextReset).getTime();
    const total = end - start;
    if (total <= 0) {
      return 0;
    }
    const elapsed = Date.now() - start;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  });

  tokenBillingEnabled = computed(
    () => this.settings()?.token_billing_enabled ?? false,
  );

  canEditOnDemand = computed(() => {
    const role = this.userService.user()?.role;
    return role === 'admin' || role === 'superuser';
  });

  isSuperuser = computed(() => this.userService.user()?.role === 'superuser');

  onDemandEnabled = computed(() => this.settings()?.on_demand_enabled ?? false);

  onDemandBudget = computed(() => {
    const budget = this.settings()?.on_demand_monthly_budget;
    return budget !== null && budget !== undefined ? Number(budget) : null;
  });

  onDemandSpend = computed(() =>
    Number(this.settings()?.on_demand_spent_this_period ?? 0),
  );

  onDemandBudgetAmount = computed(() => this.onDemandBudget() ?? 0);

  onDemandSpendProgress = computed(() => {
    const budget = this.onDemandBudgetAmount();
    if (budget <= 0) {
      return 0;
    }
    return Math.min(100, (this.onDemandSpend() / budget) * 100);
  });

  currencyCode = computed(
    () => this.settings()?.organisation_base_currency_code ?? 'GBP',
  );

  tokenOnDemandPrices = computed(() => {
    const settings = this.billingService.tokenSettings.value() ?? [];
    return settings
      .map((setting) => ({
        tokenType: setting.token_type,
        price: Number(setting.on_demand_price_per_token),
      }))
      .filter((setting) => setting.price > 0);
  });

  /** False when every token type has price 0 — treated as disabled by superuser. */
  onDemandPricingAvailable = computed(
    () => this.tokenOnDemandPrices().length > 0,
  );

  addTokensDialogVisible = false;
  onDemandSettingsDialogVisible = false;
  onDemandDialogEnableOnSave = false;

  openOnDemandSettings(): void {
    this.onDemandDialogEnableOnSave = false;
    this.onDemandSettingsDialogVisible = true;
  }

  openEnableOnDemand(): void {
    this.onDemandDialogEnableOnSave = true;
    this.onDemandSettingsDialogVisible = true;
  }

  usageFirst = 0;
  usageRows = 20;

  tokenUseEvents = computed(
    () => this.billingService.tokenUseEvents.value()?.results ?? [],
  );

  tokenUseEventsTotal = computed(
    () => this.billingService.tokenUseEvents.value()?.count ?? 0,
  );

  usageSummary = computed(
    () =>
      this.billingService.tokenUseEvents.value()?.summary ?? {
        consumed: 0,
        refunded: 0,
        failed: 0,
      },
  );

  usageLoading = computed(() => this.billingService.tokenUseEvents.isLoading());

  onTokensAdded(): void {
    this.billingService.tokenAllocations.reload();
    this.billingService.tokenUseEvents.reload();
  }

  refreshUsage(): void {
    this.billingService.tokenUseEvents.reload();
  }

  onUsagePageChange(event: PaginatorState): void {
    this.usageFirst = event.first ?? 0;
    this.usageRows = event.rows ?? 20;
    this.billingService.setTokenUseEventsPage(this.usageRows, this.usageFirst);
  }

  allocationUsagePercent(allocation: TokenAllocation): number {
    if (allocation.allocated_amount <= 0) {
      return 0;
    }
    const used = allocation.allocated_amount - allocation.allocation_remaining;
    return Math.min(
      100,
      Math.max(0, (used / allocation.allocated_amount) * 100),
    );
  }

  quantityDisplay(event: TokenUseEvent): { text: string; className: string } {
    const onDemandTotal = event.on_demand_total;
    const isRefund =
      event.use_type === TokenUseTypeKey.TOKEN_REFUND ||
      event.use_type === TokenUseTypeKey.TOKEN_REFUND_MANUAL;
    const isOnDemandMoney =
      onDemandTotal !== null &&
      (event.use_type === TokenUseTypeKey.ON_DEMAND || isRefund);

    if (isOnDemandMoney) {
      const amount = Number(onDemandTotal);
      const formatted = `${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })} ${this.currencyCode()}`;

      if (isRefund) {
        return {
          text: `+${formatted}`,
          className: 'text-green-600 font-medium',
        };
      }
      return {
        text: `-${formatted}`,
        className: 'text-red-600 font-medium',
      };
    }

    switch (event.use_type) {
      case TokenUseTypeKey.TOKEN_REFUND:
      case TokenUseTypeKey.TOKEN_REFUND_MANUAL:
        return {
          text: `+${event.quantity}`,
          className: 'text-green-600 font-medium',
        };
      case TokenUseTypeKey.TOKEN_CONSUME:
        return {
          text: `-${event.quantity}`,
          className: 'text-red-600 font-medium',
        };
      default:
        return { text: String(event.quantity), className: '' };
    }
  }

  canRefundEvent(event: TokenUseEvent): boolean {
    return (
      !event.is_refunded &&
      (event.use_type === TokenUseTypeKey.TOKEN_CONSUME ||
        event.use_type === TokenUseTypeKey.ON_DEMAND)
    );
  }

  confirmRefund(event: TokenUseEvent): void {
    if (!this.canRefundEvent(event)) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Refund token use',
      message: `Refund ${event.quantity} ${tokenTypeLabel(event.token_type)} token(s) for ${event.source_label}?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        void this.billingService.refundTokenUseEvent(event.id);
      },
    });
  }
}
